import { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { couponsApi } from '../../api/endpoints/coupons';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Toggle } from '../../components/ui/Toggle';
import { useToast } from '../../components/ui/Alert';
import type { CouponFormHandle, CouponFormPrefill } from '../../contexts/CouponFormContext';

interface CouponFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  couponId: string;
  discountType: 'percentage' | 'fixed';
  amount: string;
  maximumAmount: string;
  minPurchase: string;
  maxUses: string;
  expiryDate: string;
  startDate: string;
  description: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  couponId: '',
  discountType: 'percentage',
  amount: '',
  maximumAmount: '',
  minPurchase: '',
  maxUses: '',
  expiryDate: '',
  startDate: '',
  description: '',
  isActive: true,
};

function numOrUndef(v: string): number | undefined {
  if (!v.trim()) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export const CouponFormModal = forwardRef<CouponFormHandle, CouponFormModalProps>(function CouponFormModal({ isOpen, onClose }, ref) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Reset on the falling edge (modal closed). Resetting on open instead
  // would wipe values the AI assistant just prefilled in the same turn,
  // because both setState calls batch into a single render and the reset
  // effect fires after.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setForm(EMPTY);
      setError(null);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useImperativeHandle(ref, (): CouponFormHandle => ({
    setFields: (values: CouponFormPrefill) => {
      setForm((prev) => ({
        ...prev,
        ...(values.couponId !== undefined ? { couponId: values.couponId } : {}),
        ...(values.discountType !== undefined ? { discountType: values.discountType } : {}),
        ...(values.amount !== undefined ? { amount: String(values.amount) } : {}),
        ...(values.maximumAmount !== undefined ? { maximumAmount: String(values.maximumAmount) } : {}),
        ...(values.minPurchase !== undefined ? { minPurchase: String(values.minPurchase) } : {}),
        ...(values.maxUses !== undefined ? { maxUses: String(values.maxUses) } : {}),
        ...(values.expiryDate !== undefined ? { expiryDate: values.expiryDate } : {}),
        ...(values.startDate !== undefined ? { startDate: values.startDate } : {}),
        ...(values.description !== undefined ? { description: values.description } : {}),
        ...(values.isActive !== undefined ? { isActive: values.isActive } : {}),
      }));
    },
  }), []);

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        couponId: form.couponId.trim(),
        discountType: form.discountType,
        amount: numOrUndef(form.amount),
        minPurchase: numOrUndef(form.minPurchase),
        maxUses: numOrUndef(form.maxUses),
        expiryDate: form.expiryDate || undefined,
        startDate: form.startDate || undefined,
        description: form.description || undefined,
        isActive: form.isActive,
        applicableProducts: [],
        applicableCategories: [],
        excludedProducts: [],
        excludedCategories: [],
      };
      if (form.discountType === 'percentage') {
        const cap = numOrUndef(form.maximumAmount);
        if (cap !== undefined) payload.maximumAmount = cap;
      }
      return couponsApi.create(storeId!, payload as never);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('coupons.createSuccess', 'Coupon created'));
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t('common.error', 'Something went wrong');
      setError(msg);
    },
  });

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.couponId.trim()) {
      setError(t('coupons.codeRequired', 'Coupon code is required'));
      return;
    }
    const amt = numOrUndef(form.amount);
    if (amt === undefined || amt <= 0) {
      setError(t('coupons.amountRequired', 'Discount amount is required'));
      return;
    }
    createMutation.mutate();
  }, [form.couponId, form.amount, createMutation, t]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('coupons.addCoupon', 'Create coupon')}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} type="button">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending} type="submit" form="coupon-create-form">
            {createMutation.isPending ? t('common.saving', 'Saving...') : t('common.create', 'Create')}
          </Button>
        </div>
      }
    >
      <form id="coupon-create-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="couponId"
          label={t('coupons.code', 'Code')}
          required
          value={form.couponId}
          onChange={(e) => setForm((s) => ({ ...s, couponId: e.target.value }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            id="discountType"
            label={t('coupons.discountType', 'Discount type')}
            value={form.discountType}
            onChange={(e) => setForm((s) => ({ ...s, discountType: e.target.value as 'percentage' | 'fixed' }))}
            options={[
              { value: 'percentage', label: t('coupons.percentage', 'Percentage') },
              { value: 'fixed', label: t('coupons.fixed', 'Fixed amount') },
            ]}
          />
          <Input
            id="amount"
            label={t('coupons.amount', 'Amount')}
            type="number"
            step="0.01"
            required
            value={form.amount}
            onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
          />
        </div>
        {form.discountType === 'percentage' && (
          <Input
            id="maximumAmount"
            label={t('coupons.maximumAmount', 'Maximum discount cap (optional)')}
            type="number"
            step="0.01"
            value={form.maximumAmount}
            onChange={(e) => setForm((s) => ({ ...s, maximumAmount: e.target.value }))}
          />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input
            id="minPurchase"
            label={t('coupons.minPurchase', 'Minimum purchase')}
            type="number"
            step="0.01"
            value={form.minPurchase}
            onChange={(e) => setForm((s) => ({ ...s, minPurchase: e.target.value }))}
          />
          <Input
            id="maxUses"
            label={t('coupons.maxUses', 'Max total uses')}
            type="number"
            value={form.maxUses}
            onChange={(e) => setForm((s) => ({ ...s, maxUses: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            id="startDate"
            label={t('coupons.startDate', 'Start date')}
            type="date"
            value={form.startDate}
            onChange={(e) => setForm((s) => ({ ...s, startDate: e.target.value }))}
          />
          <Input
            id="expiryDate"
            label={t('coupons.expiryDate', 'Expiry date')}
            type="date"
            value={form.expiryDate}
            onChange={(e) => setForm((s) => ({ ...s, expiryDate: e.target.value }))}
          />
        </div>
        <Textarea
          id="description"
          label={t('coupons.description', 'Description')}
          rows={2}
          value={form.description}
          onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
        />
        <div className="flex items-center gap-3">
          <Toggle
            checked={form.isActive}
            onChange={(checked) => setForm((s) => ({ ...s, isActive: checked }))}
          />
          <span className="text-sm text-text-secondary">{t('coupons.active', 'Active')}</span>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
});
